import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../lib/cn";
import { CloseIcon } from "../lib/icons";

/**
 * Dialog — manifest §Dialog.
 *
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent>
 *       <DialogHeader>Title</DialogHeader>
 *       <DialogBody>Body copy…</DialogBody>
 *       <DialogFooter>…buttons…</DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 *
 * `DialogHeader` renders the Radix Title (screen-reader labelling for the
 * panel) and `DialogBody` the Radix Description — include both so Radix
 * ARIA wiring stays intact. `DialogContent` composes Portal + Overlay +
 * centered panel + close X button.
 */

export interface DialogProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function Dialog(props: DialogProps) {
  return <DialogPrimitive.Root {...props} />;
}
Dialog.displayName = "Dialog";

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {}

/** Portalled overlay + centered panel with a top-right close X button. */
export const DialogContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, ...props }, ref) => {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-(--z-modal) bg-surface-overlay" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-(--z-modal) w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border-default bg-surface-default p-5 shadow-xl",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-3 top-3 rounded-md p-1 text-content-secondary outline-none transition-colors duration-150 hover:bg-surface-raised hover:text-content-primary focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-default">
          <CloseIcon aria-hidden="true" className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
DialogContent.displayName = "DialogContent";

export interface DialogHeaderProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> {}

/** Panel heading — renders the Radix Title the panel is labelled by. */
export const DialogHeader = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DialogHeaderProps
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("type-h5 mb-2 pr-6 text-content-primary", className)}
      {...props}
    />
  );
});
DialogHeader.displayName = "DialogHeader";

export interface DialogBodyProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> {}

/** Supporting copy — renders the Radix Description. */
export const DialogBody = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DialogBodyProps
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("type-body-md text-content-secondary", className)}
      {...props}
    />
  );
});
DialogBody.displayName = "DialogBody";

export interface DialogFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/** Action row — end-aligned buttons with Dialog-safe spacing. */
export const DialogFooter = forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("mt-5 flex justify-end gap-2", className)}
        {...props}
      />
    );
  },
);
DialogFooter.displayName = "DialogFooter";
